// Share links — ONE module for every shareable Yalola link, web + native app.
// Mirrors backend/share_links.py: kind ↔ Open Graph page (/api/e/…) ↔ SPA route.
//   shareLinkFor(kind, id)   → the public link to send (rich card + opens the app)
//   toShareLink(url)         → converts any in-app URL into that public link (query kept)
//   toInternalPath(url)      → public link / yalola:// deep link → SPA route (native app, legacy)
//   openInNativeApp(path)    → user-initiated hand-off to the installed app (mobile web)
import { publicOrigin } from "@/lib/publicOrigin";

export const VIA_SHARE_KEY = "yalola.viaShare";
export const APP_PACKAGE = "app.yalola";
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${APP_PACKAGE}`;

const INTERNAL_QS = new Set(["v", "utm_source", "utm_medium", "utm_campaign", "fbclid"]);

const cleanQuery = (search) => {
  const sp = new URLSearchParams(search || "");
  INTERNAL_QS.forEach((k) => sp.delete(k));
  const s = sp.toString();
  return s ? `?${s}` : "";
};

const strip = (p) => (p || "/").replace(/\/+$/, "") || "/";

// kind → OG path
export const OG_PATH = {
  app: () => "/api/e/app",
  church: (id) => `/api/e/${id}`,
  post: (id) => `/api/e/post/${id}`,
  live: (id) => `/api/e/live/${id}`,
  event: (id) => `/api/e/event/${id}`,
  group: (id) => `/api/e/group/${id}`,
  user: (id) => `/api/e/user/${id}`,
  story: (id) => `/api/e/story/${id}`,
  schedule: (id) => `/api/e/schedule/${id}`,
  prayer: (id) => `/api/e/prayer/${id}`,
};

// SPA route → { kind, id } (pathname only; query-based routes handled in parseShareTarget)
const SPA_RULES = [
  ["church", /^\/churches\/([^/]+)$/],
  ["post", /^\/post\/([^/]+)$/],
  ["live", /^\/replay\/([^/]+)$/],
  ["event", /^\/events?\/([^/]+)$/],
  ["user", /^\/profile\/([^/]+)$/],
  ["group", /^\/groups?\/([^/]+)$/],
  ["schedule", /^\/live\/waiting\/([^/]+)$/],
  ["story", /^\/s\/story\/([^/]+)$/],
];

// What a Yalola URL points at: { kind, id, query } or null (external / not shareable).
export function parseShareTarget(url) {
  try {
    const origin = publicOrigin();
    const u = new URL(url, origin);
    if (u.origin !== origin && !/^https?:\/\/localhost(:\d+)?$/i.test(u.origin)) return null;
    const p = strip(u.pathname);
    const sp = new URLSearchParams(u.search);
    if (p === "/live" && sp.get("watch")) { const id = sp.get("watch"); sp.delete("watch"); return { kind: "live", id, query: cleanQuery(sp) }; }
    if (p === "/reels" && sp.get("start")) { const id = sp.get("start"); sp.delete("start"); return { kind: "post", id, query: cleanQuery(sp) }; }
    if (p === "/prayers" && sp.get("focus")) { const id = sp.get("focus"); sp.delete("focus"); return { kind: "prayer", id, query: cleanQuery(sp) }; }
    if (p === "/home" && sp.get("sid")) { const id = sp.get("sid"); sp.delete("sid"); sp.delete("story"); return { kind: "story", id, query: cleanQuery(sp) }; }
    if (p === "/" && sp.get("story")) { const id = sp.get("story"); sp.delete("story"); return { kind: "story", id, query: cleanQuery(sp) }; }
    if (p === "/" || p === "/home") return { kind: "app", id: "", query: "" };
    for (const [kind, re] of SPA_RULES) {
      const m = p.match(re);
      if (m) return { kind, id: m[1], query: cleanQuery(sp) };
    }
    return null;
  } catch {
    return null;
  }
}

export const isShareablePath = (url) => { const t = parseShareTarget(url); return !!t && t.kind !== "app"; };

// Public link for a kind/id: `${origin}/api/e/…` (+ query such as ?t= or ?photo=).
export function shareLinkFor(kind, id = "", { query = "" } = {}) {
  const build = OG_PATH[kind];
  if (!build) return publicOrigin();
  const q = typeof query === "string" ? query : `?${new URLSearchParams(query).toString()}`;
  return `${publicOrigin()}${build(encodeURIComponent(String(id)))}${cleanQuery(q)}`;
}

// Any in-app URL → its public share link. External / unknown URLs are returned unchanged.
export function toShareLink(url) {
  const t = parseShareTarget(url);
  if (!t) return url;
  return shareLinkFor(t.kind, t.id, { query: t.query });
}

// Public link (https://yalola.app/…, /api/e/…, yalola://open/…) → SPA path.
const E_MAP = [
  [/^\/api\/e\/post\/([^/]+)$/, (m) => `/post/${m[1]}`],
  [/^\/api\/e\/live\/([^/]+)$/, (m) => `/replay/${m[1]}`],
  [/^\/api\/e\/event\/([^/]+)$/, (m) => `/events/${m[1]}`],
  [/^\/api\/e\/group\/([^/]+)$/, (m) => `/groups/${m[1]}`],
  [/^\/api\/e\/user\/([^/]+)$/, (m) => `/profile/${m[1]}`],
  [/^\/api\/e\/story\/([^/]+)$/, (m) => `/s/story/${m[1]}`],
  [/^\/api\/e\/schedule\/([^/]+)$/, (m) => `/live/waiting/${m[1]}`],
  [/^\/api\/e\/prayer\/([^/]+)$/, (m) => `/prayers?focus=${m[1]}`],
  [/^\/api\/e\/app$/, () => "/home"],
  [/^\/api\/e\/([^/]+)$/, (m) => `/churches/${m[1]}`],
];

export function toInternalPath(rawUrl) {
  try {
    const u = new URL(rawUrl);
    let path;
    if (u.protocol === "yalola:") {
      // yalola://open/post/<id>?x=y → /post/<id>?x=y ("open" is just the marker host)
      path = u.pathname || "";
      if ((!path || path === "/") && u.host && u.host !== "open") path = `/${u.host}`;
    } else if (/^https?:$/.test(u.protocol)) {
      path = u.pathname;
    } else {
      return null;
    }
    path = strip(path);
    const sp = new URLSearchParams(u.search);
    if (path === "/" && sp.get("story")) return `/s/story/${sp.get("story")}`;
    const query = cleanQuery(sp);
    for (const [re, fn] of E_MAP) {
      const m = path.match(re);
      if (m) { const base = fn(m); return base + (query ? (base.includes("?") ? `&${query.slice(1)}` : query) : ""); }
    }
    if (path === "/") return "/home";
    return path + query;
  } catch {
    return null;
  }
}

// --- « Ouvrir dans l'app » (mobile web) -------------------------------------------------

export const viaSharePath = () => { try { return sessionStorage.getItem(VIA_SHARE_KEY) || ""; } catch { return ""; } };
export const clearViaShare = () => { try { sessionStorage.removeItem(VIA_SHARE_KEY); } catch { /* noop */ } };

const isAndroidUA = () => /android/i.test(navigator.userAgent);
const isIOSUA = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

// intent:// Chrome/Chromium : ouvre l'app installée (schéma yalola://, sans dépendre de la vérification App Links du
// téléphone) ; app absente → Chrome suit tout seul `fallback`.
export const appIntentUrl = (path, fallback) =>
  `intent://open${path}#Intent;scheme=yalola;package=${APP_PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallback)};end`;

const IN_APP_BROWSER_RE = /\bwv\b|WhatsApp|FBAN|FBAV|FB_IAB|Instagram|Messenger|Line\/|Snapchat|Telegram|TikTok|Twitter|MicroMessenger/i;
// Relance automatique possible : Android + Chromium (intent://), hors navigateurs intégrés (WhatsApp, Facebook…) qui
// n'exécutent pas intent:// et laisseraient une page d'erreur.
export const autoHandoffAllowed = (ua = navigator.userAgent) => /android/i.test(ua) && /Chrome\//i.test(ua) && !IN_APP_BROWSER_RE.test(ua);

const AUTO_HANDOFF_KEY = "yalola.appHandoffTried";
// Lien de contenu yalola.app ouvert dans Chrome (téléphone qui n'a pas vérifié le domaine) : on relance l'app installée
// AUTOMATIQUEMENT, une seule fois par session. App absente → Chrome recharge cette même page (repli) ; le marqueur de
// session empêche toute boucle et laisse la bannière « Ouvrir dans l'app » prendre le relais (un tap).
export function autoOpenInNativeApp(path) {
  try {
    if (sessionStorage.getItem(AUTO_HANDOFF_KEY) === "1") return false;
    sessionStorage.setItem(AUTO_HANDOFF_KEY, "1");
  } catch { return false; }
  if (!autoHandoffAllowed()) return false;
  const p = path && path.startsWith("/") ? path : "/home";
  try { window.location.replace(appIntentUrl(p, window.location.href)); } catch { return false; }
  return true;
}

// Tries to hand the current page over to the installed app. Resolves true when the page was
// hidden (app opened), false when nothing happened within `waitMs` (not installed) — in
// that case the caller may send the member to the store. Must run from a user gesture.
// waitMs : Chrome peut afficher une demande « Ouvrir Yalola ? » avant de lancer l'app → on laisse le temps de répondre.
export function openInNativeApp(path, { storeUrl = "", waitMs = 3000 } = {}) {
  const p = path && path.startsWith("/") ? path : "/home";
  return new Promise((resolve) => {
    let done = false;
    const finish = (opened) => { if (done) return; done = true; cleanup(); resolve(opened); };
    const onHide = () => { if (document.hidden) finish(true); };
    const cleanup = () => { document.removeEventListener("visibilitychange", onHide); window.removeEventListener("pagehide", onHide); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);
    setTimeout(() => finish(false), waitMs);
    try {
      if (isAndroidUA()) {
        if (/\bwv\b/i.test(navigator.userAgent)) {
          // Navigateur intégré (WhatsApp, Facebook…) : pas de intent://, le schéma suffit.
          window.location.href = `yalola://open${p}`;
          return;
        }
        // Chrome opens the app when installed and follows the fallback URL by itself otherwise.
        window.location.href = appIntentUrl(p, storeUrl || PLAY_STORE_URL);
      } else if (isIOSUA()) {
        window.location.href = `yalola://open${p}`;
      } else {
        finish(false);
      }
    } catch {
      finish(false);
    }
  });
}
